import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import CardPost from "./CardPost";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import { Row, Col, Container } from "react-bootstrap";
import AnimatedLoading from "components/Loading/aminateLoading";
import { ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import CustomPagination from "views/client/pagination";
import NoArchivedPosts from "./NoArchivedPosts";
import { PostSearchBar } from "components/searchBars/SearchBars";
import { useSnackbar } from "hooks/useSbackbar";
import { useTranslation } from "react-i18next";
import JobCardRec from "./JobCardRec";
import JobCardRecSkeleton from "./components/skeletonPost";

const ArchivedPosts = ({
  filter = "",
  filteredCards = [],
  updatePost,
  handleArchiveSwitch,
  onArchivePost,
  deletePostById,
  // handlePostClick,
  companyLogo,
  company,
}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem('archivedPostsPage');
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [totalPosts, setTotalPosts] = useState(0);
  const [isArchived, setIsArchived] = useState(true);

  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  // Save scroll position before navigating away
  const handlePostClick = (post) => {
    // const scrollPosition = window.scrollY;
    // localStorage.setItem('archivedPostsScroll', scrollPosition.toString());
    // localStorage.setItem('archivedPostsPage', page.toString());
    
    const encodedId = btoa(post.id);
    if(post.reference){
      navigate(`/talent/post/scraped/${encodedId}`);
    }else{
      navigate(`/talent/post/${encodedId}`);
    }
    localStorage.setItem("currentPostId", encodedId);
  };

  // Restore scroll position when component mounts
  // useEffect(() => {
  //   const savedScroll = localStorage.getItem('archivedPostsScroll');
  //   if (savedScroll) {
  //     window.scrollTo({
  //       top: parseInt(savedScroll),
  //       behavior: 'instant'
  //     });
  //   }
  // }, [posts]); // Run after posts are loaded

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
    localStorage.setItem('archivedPostsPage', (newPage + 1).toString());
    // Reset scroll position when changing pages
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
    localStorage.setItem('archivedPostsPage', '1');
    // Reset scroll position when changing page size
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userId = decodedToken?.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    if (!token || typeof token !== "string" || !token.includes(".")) {
      throw new Error("Invalid token format");
    }

    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      throw new Error("Token does not have enough segments");
    }

    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    try {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error parsing JWT payload", error);
      throw new Error("Failed to parse JWT payload");
    }
  }

  const userId = getUserIdFromAccessToken();

  const loadArchivedPosts = async () => {
    if (!apiUrl || !userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const endpoint = `${apiUrl}posts/list/${userId}/all/`;
      const response = await fetch(
        `${endpoint}?page=${page}&page_size=${rowsPerPage}&filter=${filter || ""}`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      setPosts(json.results || []);
      setTotalPosts(json.count || 0);
    } catch (error) {
      console.error("Error loading archived posts:", error);
      setPosts([]);
      setTotalPosts(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchivedPosts();
  }, [page, rowsPerPage, filter]);

  const onUnarchivePost = async (postId) => {
    if (!apiUrl || !userId) {
      toast.error(t("postPage.errors.archiveUpdateFailed"));
      return;
    }

    try {
      const post = posts.find((p) => p.id === postId);
      if (post && new Date(post.deadline) < new Date()) {
        toast.warning(t("postPage.warnings.deadlineChangeRequired"));
        return;
      }

      const response = await fetch(
        `${apiUrl}posts/${postId}/archive/${userId}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({ archived: false }),
        }
      );
      if (response.ok) {
        loadArchivedPosts();
        toast.success(t("postPage.success.postUnarchived"));
      } else {
        toast.error(t("postPage.errors.archiveUpdateFailed"));
      }
    } catch (error) {
      console.error(`Error updating archive status: ${error.message}`);
      toast.error(`Error updating archive status: ${error.message}`);
    }
  };

  const handleImageUpload = (postImage) => {
    // This function could be implemented if needed for editing archived posts
    return postImage;
  };

  const handleUpdatePost = async (updatedData) => {
    try {
      console.log("Updating archived post:", updatedData);
      // Extract postId and remove it from the data being sent
      const { postId, ...dataToUpdate } = updatedData;
      
      if (!postId) {
        console.error('No postId provided for update');
        toast.error(t("postPage.errors.postUpdateFailed") || "Failed to update post: No post ID");
        return false;
      }

      const response = await axios.put(
        `${apiUrl}posts/${postId}/update/`,
        dataToUpdate,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      if (response.status === 200) {
        // Instead of just reloading, update the post in the local state
        const updatedPost = response.data;
        console.log("Server response:", updatedPost);
        
        // Update the post in filteredCards if it exists there
        const updatedFilteredCards = filteredCards.map(post => 
          post.id === postId ? { ...post, ...updatedPost } : post
        );
        
        // Call loadArchivedPosts to refresh the data
        await loadArchivedPosts();
        
        toast.success(t("postPage.success.postUpdated") || "Post updated successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error updating archived post:`, error.response?.data || error.message);
      toast.error(t("postPage.errors.postUpdateFailed") || "Failed to update post");
      return false;
    }
  };

  // Make sure filteredCards is an array before rendering
  const cardsToRender = Array.isArray(filteredCards) ? filteredCards : [];
useEffect(()=>{
  console.log("filteredCards==>", filteredCards);
},[filteredCards])
  return (
    <ThemeProvider theme={theme}>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="d-flex flex-column content">
        <Container>
          <Container fluid className="px-4">
            <Row className="g-4">
              {loading ? (
                [...Array(6)].map((_, index) => (
                  <Col
                    key={`skeleton-${index}`}
                    xs={12}
                    sm={6}
                    md={4}
                    lg={4}
                    xl={4}
                    xxl={3}
                  >
                    <JobCardRecSkeleton />
                  </Col>
                ))
              ) : (
                posts.map((post) => (
                  <Col key={post.id} xs={12} sm={6} md={4} lg={4} xl={4}>
                    <JobCardRec
                      job={post}
                      postid={post.id}
                      imageSrc={post.image}
                      title={post.title}
                      description={post.description}
                      requirement={post.requirements}
                      deadline={post.deadline}
                      company={post.company}
                      companyLogo={companyLogo}
                      onDeletePost={deletePostById}
                      updatePost={handleUpdatePost}
                      handleImageUpload={handleImageUpload}
                      archived={post.archived}
                      onArchivePost={onUnarchivePost}
                      onSelectPost={() => handlePostClick(post)}
                      isArchivedView={true}
                      isArchived={isArchived}
                    />
                  </Col>
                ))
              )}
              {!loading && posts.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6">
                    <NoArchivedPosts text={true} />
                  </Typography>
                </Box>
              )}
            </Row>
          </Container>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              padding: "26px 0",
            }}
          >
            {totalPosts > 0 && (
              <CustomPagination
                page={page - 1}
                handleChangePage={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                count={totalPosts}
              />
            )}
          </Box>
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default ArchivedPosts;
